/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useEffect, useMemo } from 'react'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { useModalScroll } from 'src/hooks/useModalScroll'
import useWindowSize from 'src/hooks/useWindowSize'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type PayInFullModalProps = {
  isOpen: boolean
  onClose: () => void
  /** Kept for backward compat with the parent; the new flow never mutates here so it is unused. */
  refetch: () => void
  registrationId: string
  /** Kept for backward compat with the parent; the toggle was removed (receipt auto-sends on success). */
  email: string
  /** Display-only: the server recalculates the consolidated amount authoritatively */
  totalFormatted: string
  replacedCount: number
}

/**
 * Confirms a "Pay Full Balance" and routes to the /pay screen to take payment now. NOTHING is
 * consolidated here — the deposit/installment rows are untouched until the payment succeeds
 * (the server consolidates them into one paid Full Payment row only then, via the Stripe
 * webhook or the SportsPay charge). The receipt is sent automatically on success.
 */
const PayInFullModal = ({
  isOpen,
  onClose,
  registrationId,
  totalFormatted,
  replacedCount,
}: PayInFullModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { openModal, closeModal } = useModalScroll()

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  const handlePayNow = useCallback(() => {
    // Take payment on the /pay screen, keyed on the team registration. The balance is only
    // consolidated after the payment succeeds — opening this screen mutates nothing.
    window.open(`/pay?teamRegistrationId=${registrationId}`)
    onClose()
  }, [onClose, registrationId])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '40vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : '40vw'}
      closeIcon={
        <CloseOutlined
          css={css`
            color: ${colors.SOLITUDE};
            font-size: 18px;
            cursor: pointer;
            transition: all 0.2s;
            &:hover {
              color: ${colors.WHITE};
            }
          `}
        />
      }
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Pay Full Balance
        </Text>
      }
      footer={
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: ${isMobile ? '100%' : '50%'};
            margin-left: ${isMobile ? '0' : 'auto'};
            @media (max-width: 768px) {
              width: 100%;
              flex-direction: column;
            }
          `}
        >
          <Button text='Cancel' onClick={handleCancel} variant='secondary' />
          <Button text='Pay Now' onClick={handlePayNow} variant='primary' />
        </div>
      }
      open={isOpen}
      onCancel={handleCancel}
      centered={!isMobile}
      maskClosable={false}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0 8px;
        `}
      >
        <Text variant='paragraphMedium' color={colors.WHITE}>
          Settle {replacedCount} scheduled payment
          {replacedCount === 1 ? '' : 's'}. Taxes are shown on the payment
          screen. Payments that were already paid, refunded, or have charge
          history are not affected, and nothing changes until the payment
          succeeds.
        </Text>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px;
            border-radius: 8px;
            background: ${colors.SECONDARY_200};
          `}
        >
          <Text
            variant='paragraphMedium'
            weight='semiBold'
            color={colors.WHITE}
          >
            Remaining Unpaid Balance
          </Text>
          <Text variant='heading4' weight='semiBold' color={colors.WHITE}>
            {totalFormatted}
          </Text>
        </div>
      </div>
    </Modal>
  )
}

export default PayInFullModal
